import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { AlertController } from '@ionic/angular/standalone';
import { Store } from '@ngrx/store';
import { formatISO } from 'date-fns';
import { ItemValueComponent } from 'src/app/components/item-value/item-value.component';
import { ItemComponent } from 'src/app/components/item/item.component';
import { SvgIconComponent } from 'src/app/components/svg-icon/svg-icon.component';
import { ClinicalNotesService } from 'src/app/services/clinical-notes.service';
import { ErrorService } from 'src/app/services/error.service';
import { IdService } from 'src/app/services/id.service';
import { attachmentUploadCompleted, attachmentUploadFailed, attachmentUploadInitiated, attachmentUploadProgressed } from 'src/app/store/patient-clinical-note/patient-clinical-note.actions';
import { AppState } from 'src/app/store/reducers';

@Component({
	selector: 'app-attachment-upload',
	templateUrl: './attachment-upload.component.html',
	styleUrl: './attachment-upload.component.scss',
	imports: [
		ItemComponent, ItemValueComponent, SvgIconComponent
	],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class AttachmentUploadComponent {

	@Input() clinicalNoteId!: string;

	constructor(private alertController: AlertController, private clinicalNotesService: ClinicalNotesService, private errorService: ErrorService, private idService: IdService, private store: Store<AppState>) {
	}

	async fileSelected(event: Event) {
		const input = event.target as HTMLInputElement;
		if (input.files && input.files.length) {

			// decided to handle the http request in this component as a file is not serializable (cannot be a an ngrx action property and subsequently handled in an effect)

			const file = input.files[0];

			if (file.size === 0) {
				const alert = await this.alertController.create({
					message: 'File is empty',
					buttons: ['Ok'],
					backdropDismiss: false
				});
				await alert.present();
				return;
			}

			if (file.size > 20971520) {
				const alert = await this.alertController.create({
					message: 'File size exceeds the maximum allowed size of 20MB',
					buttons: ['Ok'],
					backdropDismiss: false
				});
				await alert.present();
				return;
			}

			const attachmentId = this.idService.generateGuid();

			this.store.dispatch(attachmentUploadInitiated({ id: attachmentId, name: this.getFilenameWithoutExtension(file.name), createdDate: formatISO(new Date()) }));

			this.clinicalNotesService.uploadAttachment(this.clinicalNoteId, attachmentId, file).subscribe({
				next: (event) => {
					switch (event.type) {
						case HttpEventType.UploadProgress:
							this.store.dispatch(attachmentUploadProgressed({ id: attachmentId, loaded: event.loaded, total: event.total ?? 0 }));
							break;
						case HttpEventType.Response:
							this.store.dispatch(attachmentUploadCompleted({ id: attachmentId }));
							break;
					}
				},
				error: (error: HttpErrorResponse) => {
					this.store.dispatch(attachmentUploadFailed({ id: attachmentId, errorMessage: this.errorService.getErrorMessage(error) }));
				}
			});
		}
	}

	private getFilenameWithoutExtension(filePath: string): string {
		const filename = filePath.split(/[\\/]/).pop() ?? '';
		return filename.replace(/\.[^/.]+$/, '');
	}
}